
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as XLSX from 'xlsx';
import { Download, Upload } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export function StaffBulkUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.schoolId) {
      toast({
        title: "Error",
        description: "Please select a file and ensure you're logged in",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setProcessingStep('Reading file...');
    
    try {
      // Read file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

      if (jsonData.length === 0) {
        throw new Error('The Excel file is empty. Please use the template and ensure it contains data.');
      }

      setProgress(20);
      setProcessingStep('Validating data...');

      // Validate all rows first
      const validRows: any[] = [];
      const errorRows: any[] = [];
      
      jsonData.forEach((row, index) => {
        try {
          // Check if required fields exist and are not empty
          if (!row.NAME || typeof row.NAME !== 'string' || row.NAME.trim() === '') {
            errorRows.push({
              'Row Number': index + 2,
              'Original Name': row.NAME || '',
              'Original Category': row.CATEGORY || '',
              'Error Message': 'NAME is required and must be a non-empty string'
            });
            return;
          }

          if (!row.CATEGORY || typeof row.CATEGORY !== 'string') {
            errorRows.push({
              'Row Number': index + 2,
              'Original Name': row.NAME || '',
              'Original Category': row.CATEGORY || '',
              'Error Message': 'CATEGORY is required and must be a string'
            });
            return;
          }

          const category = row.CATEGORY.toString().trim().toUpperCase();
          if (!['TEACHING_STAFF', 'NON_TEACHING_STAFF', 'ADMIN'].includes(category)) {
            errorRows.push({
              'Row Number': index + 2,
              'Original Name': row.NAME || '',
              'Original Category': row.CATEGORY || '',
              'Error Message': `Invalid category "${category}". Must be TEACHING_STAFF, NON_TEACHING_STAFF, or ADMIN`
            });
            return;
          }

          // Validate optional fields if present
          if (row.RESIDENCE !== undefined && (typeof row.RESIDENCE !== 'string' || row.RESIDENCE.trim() === '')) {
            errorRows.push({
              'Row Number': index + 2,
              'Original Name': row.NAME || '',
              'Original Category': row.CATEGORY || '',
              'Error Message': 'RESIDENCE must be a non-empty string if provided'
            });
            return;
          }

          if (row.CONTACT !== undefined && (typeof row.CONTACT !== 'string' || row.CONTACT.trim() === '')) {
            errorRows.push({
              'Row Number': index + 2,
              'Original Name': row.NAME || '',
              'Original Category': row.CATEGORY || '',
              'Error Message': 'CONTACT must be a non-empty string if provided'
            });
            return;
          }

          validRows.push({
            name: row.NAME.trim(),
            category: category,
            residence: row.RESIDENCE ? row.RESIDENCE.trim() : null,
            contact: row.CONTACT ? row.CONTACT.trim() : null,
            school_id: user.schoolId,
          });
        } catch (err) {
          console.error(`Error processing row ${index + 2}:`, err);
          errorRows.push({
            'Row Number': index + 2,
            'Original Name': row.NAME || '',
            'Original Category': row.CATEGORY || '',
            'Error Message': err instanceof Error ? err.message : 'Unknown error processing row'
          });
        }
      });

      if (validRows.length === 0) {
        // Generate error report with all validation errors
        if (errorRows.length > 0) {
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(errorRows);
          XLSX.utils.book_append_sheet(wb, ws, 'Validation Errors');
          XLSX.writeFile(wb, 'staff_upload_errors.xlsx');
          throw new Error('No valid rows found in the file. Please check the error report for details.');
        } else {
          throw new Error('No valid rows found in the file. Please ensure your file follows the template format.');
        }
      }

      setProgress(40);
      setProcessingStep('Checking for duplicates...');

      // Bulk check for duplicates
      const { data: existingStaff, error: checkError } = await supabase
        .from('community_members')
        .select('name')
        .eq('school_id', user.schoolId)
        .in('name', validRows.map(row => row.name));

      if (checkError) {
        console.error('Error checking duplicates:', checkError);
        throw checkError;
      }

      const existingNames = new Set((existingStaff || []).map(staff => staff.name.toLowerCase()));
      const staffToInsert = validRows.filter(row => !existingNames.has(row.name.toLowerCase()));
      
      // Add duplicate errors
      validRows.forEach((row, index) => {
        if (existingNames.has(row.name.toLowerCase())) {
          errorRows.push({
            'Row Number': index + 2,
            'Original Name': row.name,
            'Original Category': row.category,
            'Error Message': `Staff member "${row.name}" already exists`
          });
        }
      });

      setProgress(60);
      setProcessingStep('Inserting new staff...');

      // Bulk insert valid staff
      if (staffToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('community_members')
          .insert(staffToInsert);

        if (insertError) {
          console.error('Error inserting staff:', insertError);
          throw insertError;
        }
      }

      setProgress(80);
      setProcessingStep('Generating report...');

      // Generate error report if needed
      if (errorRows.length > 0) {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(errorRows);
        
        // Add column widths for better readability
        ws['!cols'] = [
          { wch: 10 },  // Row Number
          { wch: 30 },  // Original Name
          { wch: 20 },  // Original Category
          { wch: 50 },  // Error Message
        ];
        
        XLSX.utils.book_append_sheet(wb, ws, 'Upload Errors');
        XLSX.writeFile(wb, 'staff_upload_errors.xlsx');

        toast({
          title: staffToInsert.length > 0 ? "Partial Success" : "Upload Failed",
          description: `Successfully added ${staffToInsert.length} staff members. Failed to add ${errorRows.length} staff members. Error report has been downloaded.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: `Successfully added all ${staffToInsert.length} staff members.`,
        });
      }

      setProgress(100);
    } catch (error) {
      console.error('Error uploading staff:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process the file. Please check the format and try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setProgress(0);
      setProcessingStep('');
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    
    // Create template sheet with example data
    const templateData = [
      {
        NAME: 'John Doe',
        CATEGORY: 'TEACHING_STAFF',
        RESIDENCE: 'Block A',
        CONTACT: '+1234567890'
      },
      {
        NAME: 'Jane Smith',
        CATEGORY: 'NON_TEACHING_STAFF',
        RESIDENCE: 'Block B',
        CONTACT: '+0987654321'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);

    // Add column widths for better readability
    ws['!cols'] = [
      { wch: 20 }, // NAME
      { wch: 15 }, // CATEGORY
      { wch: 15 }, // RESIDENCE
      { wch: 15 }, // CONTACT
    ];

    // Create validation sheet
    const validationData = [
      ['Valid Categories'],
      ['TEACHING_STAFF'],
      ['NON_TEACHING_STAFF'],
      ['ADMIN']
    ];

    const validationWs = XLSX.utils.aoa_to_sheet(validationData);

    // Add both sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.utils.book_append_sheet(wb, validationWs, 'Valid Values');

    XLSX.writeFile(wb, 'staff_upload_template.xlsx');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <Input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          disabled={isUploading}
        />
        {isUploading && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground">
              {processingStep} {progress}%
            </p>
          </div>
        )}
      </div>
      <Button
        variant="outline"
        onClick={downloadTemplate}
        className="w-full sm:w-auto"
        disabled={isUploading}
      >
        <Download className="mr-2 h-4 w-4" />
        Download Template
      </Button>
      <div className="text-sm text-muted-foreground">
        <p>Upload an Excel file with staff information using the template format:</p>
        <ul className="list-disc pl-5 mt-2">
          <li><strong>Required columns:</strong> NAME, CATEGORY</li>
          <li><strong>Optional columns:</strong> RESIDENCE, CONTACT</li>
          <li><strong>Valid CATEGORY values:</strong> TEACHING_STAFF, NON_TEACHING_STAFF, ADMIN</li>
        </ul>
      </div>
    </div>
  );
}
